import { create } from 'zustand'
import type { User } from '../lib/types'
import { authApi, attemptSilentRefresh, tokenManager, extractApiError } from '../lib/api'
import { useChatUIStore, useConversationStore } from './chatStore'
import { identifyUser, resetUser, getUtmParams } from '../lib/analytics'
import type { TierName } from '../lib/tiers'

/**
 * Auth Store State
 */
interface AuthState {
  // Current user
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Actions
  bootstrap: () => Promise<void>
  loadUser: () => Promise<void>
  setAuthenticatedUser: (user: User) => void
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string) => Promise<void>
  updateProfile: (data: {
    name?: string
    email?: string
    timezone?: string
    currentPassword?: string
    newPassword?: string
    tier?: TierName
  }) => Promise<void>
  logout: () => Promise<void>
  clearError: () => void
}

/**
 * Zustand Auth Store
 *
 * Auth state is rehydrated on app boot via `bootstrap()`, which silently
 * exchanges the refresh-token cookie for a fresh in-memory access token.
 * Initial state assumes "checking" — AuthGuard renders the loading spinner
 * until bootstrap resolves.
 */
export const useAuthStore = create<AuthState>((set) => ({
  // Initial state — `isLoading: true` so AuthGuard waits for bootstrap()
  // before deciding to redirect. Flipped false at the end of bootstrap.
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  /**
   * App-boot rehydration. Exchanges the refresh cookie (if any) for an
   * in-memory access token, then loads the user. Idempotent — safe to call
   * more than once (the underlying refresh call is single-flight).
   */
  bootstrap: async () => {
    const r = await attemptSilentRefresh()
    if (!r.ok) {
      set({ user: null, isAuthenticated: false, isLoading: false })
      return
    }

    // D — Parallel prefetch. Warm the conversation list now, while ChatPage's
    // lazy chunk is still loading, so it's ready by the time the page mounts.
    // Deliberately not awaited (fire-and-forget) — loadConversations catches its
    // own errors and never rejects. Deduped with ChatPage's own call via the
    // store's single-flight guard, collapsing to ONE GET /api/conversations.
    useConversationStore.getState().loadConversations()

    // Steady state: /api/auth/refresh returns the user inline (perf/fast-reload A),
    // so we set the full session in ONE pass with NO second /me round-trip. This
    // also lets AuthGuard take its `isAuthenticated && user` branch immediately,
    // skipping its own redundant loadUser()→/me fetch (arch #6).
    if (r.user) {
      useAuthStore.getState().setAuthenticatedUser(r.user)
      return
    }

    // Rollout safety only: a new frontend hitting an OLD control-plane that still
    // returns `{ token }` with no user. Never reached in steady state — fall back
    // to /me so auth stays intact through the deploy window.
    set({ isAuthenticated: true })
    try {
      const response = await authApi.me()
      // `'user' in data` narrows the session arm: `data` is a union, and
      // `success && data` alone does not tell the compiler which arm it got.
      if (response.success && response.data && 'user' in response.data) {
        useAuthStore.getState().setAuthenticatedUser(response.data.user)
      } else {
        tokenManager.remove()
        set({ user: null, isAuthenticated: false, isLoading: false })
      }
    } catch {
      tokenManager.remove()
      set({ user: null, isAuthenticated: false, isLoading: false })
    }
  },

  /**
   * Load current user from API
   */
  loadUser: async () => {
    // bootstrap() owns initial rehydration; loadUser() is only called after
    // an in-memory access token is known to exist (post-login flows + a few
    // legacy call sites). If there's no token, no-op cleanly.
    if (!authApi.isAuthenticated()) {
      set({ user: null, isAuthenticated: false })
      return
    }

    // Skip if user is already loaded (prevents AuthGuard/ChatPage remount loop)
    if (useAuthStore.getState().user) return

    set({ isLoading: true, error: null })

    try {
      const response = await authApi.me()

      if (response.success && response.data && 'user' in response.data) {
        const u = response.data.user
        set({
          user: u,
          isAuthenticated: true,
          isLoading: false
        })
        // Identify user in PostHog for event stitching
        // S1.17.3: Include UTM params as $set_once to preserve first-touch attribution
        const utm = getUtmParams()
        identifyUser(
          u.id,
          { email: u.email, plan: u.tier, name: u.name ?? null },
          {
            initial_utm_source: utm.utm_source,
            initial_utm_medium: utm.utm_medium,
            initial_utm_campaign: utm.utm_campaign,
          }
        )
      } else {
        throw new Error('Failed to load user')
      }
    } catch (error) {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load user'
      })
    }
  },

  /**
   * Set the authenticated user directly from a login response, with no extra
   * round-trip. Used by the Google OAuth flow: the /google-callback response
   * already returns the full user (field-parity with /api/auth/me as of Fix 3),
   * so re-fetching via loadUser() → /api/auth/me was a redundant serial round-trip.
   * Mirrors what login()/signup() already do for email/password.
   */
  setAuthenticatedUser: (user: User) => {
    set({ user, isAuthenticated: true, isLoading: false, error: null })
    // Preserve PostHog identify (previously done inside loadUser) for event stitching.
    const utm = getUtmParams()
    identifyUser(
      user.id,
      { email: user.email, plan: user.tier, name: user.name ?? null },
      {
        initial_utm_source: utm.utm_source,
        initial_utm_medium: utm.utm_medium,
        initial_utm_campaign: utm.utm_campaign,
      }
    )
  },

  /**
   * Log in user
   * CRITICAL: Resets chat store before login to prevent data leaks from previous users
   */
  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null })

    // Reset chat store before login to clear any stale data from previous user
    useChatUIStore.getState().reset()

    try {
      const response = await authApi.login({ email, password })

      if (response.success && response.data && 'user' in response.data) {
        set({
          user: response.data.user,
          isAuthenticated: true,
          isLoading: false
        })
      } else {
        throw new Error(extractApiError(response, 'Login failed'))
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Login failed',
        isLoading: false,
        isAuthenticated: false
      })
      throw error
    }
  },

  /**
   * Sign up new user
   * CRITICAL: Resets chat store before signup to prevent data leaks from previous users
   */
  signup: async (email: string, password: string) => {
    set({ isLoading: true, error: null })

    // Reset chat store before signup to clear any stale data from previous user
    useChatUIStore.getState().reset()

    try {
      // S1.17.3: Include UTM params in signup request for CAC tracking
      const utm = getUtmParams()
      const response = await authApi.signup({
        email,
        password,
        utmSource: utm.utm_source,
        utmMedium: utm.utm_medium,
        utmCampaign: utm.utm_campaign,
      })

      if (response.success && response.data && 'user' in response.data) {
        set({
          user: response.data.user,
          isAuthenticated: true,
          isLoading: false
        })
      } else {
        throw new Error(extractApiError(response, 'Signup failed'))
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Signup failed',
        isLoading: false,
        isAuthenticated: false
      })
      throw error
    }
  },

  /**
   * Update user profile
   */
  updateProfile: async (data: {
    name?: string
    email?: string
    timezone?: string
    currentPassword?: string
    newPassword?: string
    tier?: TierName
  }) => {
    // Note: intentionally NOT setting isLoading here — that flag is for initial
    // auth checking and triggers AuthGuard's full-screen spinner, which would
    // unmount the Settings page and lose all form state.
    set({ error: null })

    try {
      const response = await authApi.updateProfile(data)

      if (response.success && response.data && 'user' in response.data) {
        set({ user: response.data.user })
      } else {
        throw new Error(extractApiError(response, 'Profile update failed'))
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Profile update failed',
      })
      throw error
    }
  },

  /**
   * Log out user.
   *
   * Awaits the server-side revoke so the browser processes Set-Cookie
   * (clearing the refresh cookie) BEFORE we navigate. Then resets all
   * client-side state.
   */
  logout: async () => {
    await authApi.logout()
    // Reset chat store to prevent new users from seeing old data
    useChatUIStore.getState().reset()
    // Reset PostHog identity
    resetUser()
    // Clear verification banner dismissal state
    localStorage.removeItem('verification_banner_dismissed_at')
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null
    })
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null })
  }
}))
